import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Bot, MessageSquare, DollarSign, BookOpen, BarChart3 } from 'lucide-react';
import { mockAgents, mockCostData, mockHourlyActivity } from '../data/mockData';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
export function OverviewPage() {
  const navigate = useNavigate();
  const totalAgents = mockAgents.length;
  const activeAgents = mockAgents.length; // Mock: all active
  const totalConversations = mockCostData.conversationCosts.length;
  const totalCostMonth = mockCostData.costPerAgent.reduce((sum, agent) => sum + agent.totalCostEur, 0);
  const topAgentsByUsage = mockCostData.costPerAgent.slice(0, 3);
  // Mock data for pie chart - App usage distribution
  const appUsageData = mockCostData.costPerAgent.map(agent => ({
    name: agent.agentName,
    value: agent.totalCostEur,
    percentage: agent.totalCostEur / totalCostMonth * 100
  }));
  const maxCalls = Math.max(...mockHourlyActivity.map(d => d.calls));
  // Colors for pie chart - GREEN LIME palette
  const pieChartColors = ['#91c429', '#7aa623', '#63881d', '#a3d645', '#b8e05f' // green lime lighter
  ];
  const pieLegendColors = ['bg-blue-500', 'bg-blue-600', 'bg-blue-700', 'bg-blue-400', 'bg-blue-300'];
  return <div className="pb-12 space-y-8" data-id="element-1619">
      {/* KPI Cards - All green lime theme */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1620">
        <AppCard className="flex flex-col justify-between" data-id="element-1621">
          <div className="flex items-start justify-end mb-2" data-id="element-1622">
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-dark-subtle px-2 py-1 rounded-full" data-id="element-1623">
              Totali
            </span>
          </div>
          <div data-id="element-1624">
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1625">
              {activeAgents}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1626">
              Service attivi su {totalAgents}
            </p>
          </div>
        </AppCard>

        <AppCard className="flex flex-col justify-between" data-id="element-1627">
          <div className="flex items-start justify-end mb-2" data-id="element-1628">
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full" data-id="element-1629">
              +12%
            </span>
          </div>
          <div data-id="element-1630">
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1631">
              {totalConversations}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1632">
              Conversazioni totali
            </p>
          </div>
        </AppCard>

        <AppCard className="flex flex-col justify-between" data-id="element-1633">
          <div className="flex items-start justify-end mb-2" data-id="element-1634">
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-dark-subtle px-2 py-1 rounded-full" data-id="element-1635">
              Gennaio
            </span>
          </div>
          <div data-id="element-1636">
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1637">
              €{totalCostMonth.toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1638">
              Costo totale mese
            </p>
          </div>
        </AppCard>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1639">
        {/* Pie Chart - App Usage Distribution */}
        <AppCard padding="none" data-id="element-1640">
          <div className="p-6 pb-5" data-id="element-1641">
            <div className="mb-6" data-id="element-1642">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1643">
                Distribuzione utilizzo Service
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1644">
                Per costo totale
              </p>
            </div>

            {/* Chart and Legend Side by Side */}
            <div className="flex items-center gap-8" data-id="element-1645">
              {/* Pie Chart - Left */}
              <div className="flex-shrink-0" data-id="element-1646">
                <div className="relative w-48 h-48" data-id="element-1647">
                  {/* Simple pie chart using conic-gradient */}
                  <div className="w-full h-full rounded-full" style={{
                  background: `conic-gradient(
                        ${appUsageData.map((app, idx) => {
                    const startPercent = appUsageData.slice(0, idx).reduce((sum, a) => sum + a.percentage, 0);
                    const endPercent = startPercent + app.percentage;
                    return `${pieChartColors[idx % pieChartColors.length]} ${startPercent}% ${endPercent}%`;
                  }).join(', ')}
                      )`
                }} data-id="element-1648">
                    <div className="absolute inset-4 bg-white dark:bg-dark-surface rounded-full flex items-center justify-center" data-id="element-1649">
                      <div className="text-center" data-id="element-1650">
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-id="element-1651">
                          {appUsageData.length}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1652">
                          App AI
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legend - Right (Compact) */}
              <div className="flex-1 space-y-3" data-id="element-1653">
                {appUsageData.map((app, idx) => <div key={app.name} className="flex items-center justify-between gap-2" data-id="element-1654">
                    <div className="flex items-center flex-1 min-w-0" data-id="element-1655">
                      <div className={`w-3 h-3 rounded-full ${pieLegendColors[idx % pieLegendColors.length]} mr-2 flex-shrink-0`} data-id="element-1656" />
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate" data-id="element-1657">
                        {app.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1658">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-1659">
                        {app.percentage.toFixed(1)}%
                      </span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100" data-id="element-1660">
                        €{app.value.toFixed(2)}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </AppCard>

        {/* Line Chart - Hourly Activity */}
        <AppCard padding="none" data-id="element-1661">
          <div className="p-6 pb-5" data-id="element-1662">
            <div className="mb-6" data-id="element-1663">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1664">
                Attività oraria
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1665">
                Chiamate API nelle ultime 24 ore
              </p>
            </div>

            <div className="relative h-64" data-id="element-1666">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2" data-id="element-1667">
                <span data-id="element-1668">{maxCalls}</span>
                <span data-id="element-1669">{Math.round(maxCalls * 0.75)}</span>
                <span data-id="element-1670">{Math.round(maxCalls * 0.5)}</span>
                <span data-id="element-1671">{Math.round(maxCalls * 0.25)}</span>
                <span data-id="element-1672">0</span>
              </div>

              {/* Chart area - fixed height calculation */}
              <div className="ml-8 absolute top-0 bottom-8 left-0 right-0 flex items-end justify-between gap-0.5" data-id="element-1673">
                {mockHourlyActivity.map((data, idx) => {
                const heightPercent = data.calls / maxCalls * 100;
                const isHighActivity = data.calls > maxCalls * 0.7;
                return <div key={data.hour} className="flex-1 flex flex-col justify-end group relative h-full" data-id="element-1674">
                      <div className={`w-full rounded-t transition-all duration-300 ${isHighActivity ? 'bg-blue-500 dark:bg-blue-600' : 'bg-blue-300 dark:bg-blue-700'} hover:bg-blue-600 dark:hover:bg-blue-500 cursor-pointer`} style={{
                    height: `${heightPercent}%`,
                    minHeight: '2px'
                  }} data-id="element-1675">
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10" data-id="element-1676">
                          {data.hour}: {data.calls} chiamate
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              {/* X-axis labels */}
              <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-1677">
                {mockHourlyActivity.filter((_, idx) => idx % 4 === 0).map(data => <span key={data.hour} className="flex-1 text-center" data-id="element-1678">
                      {data.hour}
                    </span>)}
              </div>
            </div>
          </div>
        </AppCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1679">
        {/* Top Agents by Usage */}
        <AppCard padding="none" className="h-full" data-id="element-1680">
          <div className="p-6 pb-5" data-id="element-1681">
            <div className="flex items-center justify-between mb-6" data-id="element-1682">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1683">
                Top Service per utilizzo
              </h2>
              <AppButton variant="ghost" size="sm" onClick={() => navigate('/app-ai')} className="text-xs" data-id="element-1684">
                Vedi tutti
              </AppButton>
            </div>

            <div className="space-y-3" data-id="element-1685">
              {topAgentsByUsage.map((agent, index) => <div key={agent.agentId} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors cursor-pointer group" onClick={() => navigate(`/app-ai/${agent.agentId}/istruzioni`)} data-id="element-1686">
                  <div className="flex items-center flex-1" data-id="element-1687">
                    <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-dark-subtle flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400 mr-3" data-id="element-1688">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1689">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-1690">
                        {agent.agentName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1691">
                        {agent.month}
                      </p>
                    </div>
                  </div>
                  <div className="text-right ml-4" data-id="element-1692">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1693">
                      €{agent.totalCostEur.toFixed(2)}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </AppCard>

        {/* Recent Activity */}
        <AppCard className="h-full" data-id="element-1694">
          <div className="flex items-center justify-between mb-6" data-id="element-1695">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1696">
              Attività recente
            </h2>
            <AppButton variant="ghost" size="sm" onClick={() => navigate('/storico')} className="text-xs" data-id="element-1697">
              Vedi storico
            </AppButton>
          </div>

          <div className="space-y-1" data-id="element-1698">
            {mockCostData.conversationCosts.slice(0, 4).map(conv => <div key={conv.id} className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors cursor-pointer group" data-id="element-1699">
                <div className="flex-1 min-w-0" data-id="element-1700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-1701">
                    {conv.agentName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1702">
                    {conv.userLabel} •{' '}
                    {new Date(conv.timestamp).toLocaleString('it-IT', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </p>
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-subtle px-2 py-1 rounded-full" data-id="element-1703">
                  €{conv.costEur.toFixed(2)}
                </span>
              </div>)}
          </div>
        </AppCard>
      </div>

      {/* Quick Actions - All green lime theme */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1704">
        <button onClick={() => navigate('/policy')} className="flex items-center p-5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-2xl hover:shadow-md hover:border-gray-300 dark:hover:border-dark-medium transition-all group text-left" data-id="element-1705">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200" data-id="element-1706">
            <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1707" />
          </div>
          <div data-id="element-1708">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-0.5" data-id="element-1709">
              Gestisci policy
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1710">
              Regole e vincoli globali
            </p>
          </div>
        </button>

        <button onClick={() => navigate('/conoscenza')} className="flex items-center p-5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-2xl hover:shadow-md hover:border-gray-300 dark:hover:border-dark-medium transition-all group text-left" data-id="element-1711">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200" data-id="element-1712">
            <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1713" />
          </div>
          <div data-id="element-1714">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-0.5" data-id="element-1715">
              Knowledge base
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1716">
              Sorgenti di conoscenza
            </p>
          </div>
        </button>

        <button onClick={() => navigate('/costi')} className="flex items-center p-5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-2xl hover:shadow-md hover:border-gray-300 dark:hover:border-dark-medium transition-all group text-left" data-id="element-1717">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200" data-id="element-1718">
            <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1719" />
          </div>
          <div data-id="element-1720">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-0.5" data-id="element-1721">
              Analisi costi
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1722">
              Monitora spese e limiti
            </p>
          </div>
        </button>
      </div>
    </div>;
}